import React, { useState, useEffect, useCallback } from "react";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { Link } from "react-router-dom";
import { Spin, message, Modal } from "antd";
import Sidebar from "../../Sidebar";
import Header from "../../Header";
import { morning_img_01 } from "../../imagepath";
import nhisApi from "../../../api/nhis.api";

// Backend sends LocalDateTime (UTC, no offset) — append 'Z' and render in Sofia.
const toUtc = (s) => (s ? (s.endsWith("Z") ? s : s + "Z") : null);

const fmtDateTime = (s) => {
  const u = toUtc(s);
  if (!u) return "—";
  const d = new Date(u);
  const date = d.toLocaleDateString("bg-BG", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Sofia" });
  const time = d.toLocaleTimeString("bg-BG", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Sofia" });
  return `${date} ${time}`;
};

const fmtRelative = (s) => {
  const u = toUtc(s);
  if (!u) return "—";
  const diffMs = Date.now() - new Date(u).getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (diffMs < 60000) return "току-що";
  if (mins < 60) return `преди ${mins} мин`;
  if (hours < 24) return `преди ${hours} ч`;
  return `преди ${days} дни`;
};

// eslint-disable-next-line react/prop-types
const Badge = ({ tone, children }) => {
  const map = {
    success: "bg-success",
    danger: "bg-danger",
    warning: "bg-warning text-dark",
    neutral: "bg-secondary",
  };
  return <span className={`badge ${map[tone] || map.neutral}`}>{children}</span>;
};

const certBadge = (status) => {
  switch (status) {
    case "VALID": return <Badge tone="success">Валиден</Badge>;
    case "EXPIRING": return <Badge tone="warning">Изтича скоро</Badge>;
    case "EXPIRED": return <Badge tone="danger">Изтекъл</Badge>;
    default: return <Badge tone="neutral">Няма данни</Badge>;
  }
};

const Admin_Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({}); // keyed action-in-progress flags

  // Error drill-in modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const load = useCallback(async (silent) => {
    try {
      if (!silent) setLoading(true);
      const snapshot = await nhisApi.getMonitoring();
      setData(snapshot);
    } catch (error) {
      console.error("Error loading monitoring snapshot:", error);
      message.error("Грешка при зареждане на мониторинга");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setBusyFlag = (key, val) => setBusy((prev) => ({ ...prev, [key]: val }));

  const handleRetry = async (job) => {
    setBusyFlag(`job:${job.jobId}`, true);
    try {
      await nhisApi.retryJob(job.jobId);
      message.success("Задачата е върната в опашката");
      await load(true);
    } catch (error) {
      message.error(error?.data?.error || "Грешка при повторно изпълнение");
    } finally {
      setBusyFlag(`job:${job.jobId}`, false);
    }
  };

  const handleReset = async (circuit) => {
    const key = `${circuit.bridgeId}:${circuit.flow}`;
    setBusyFlag(`circuit:${key}`, true);
    try {
      await nhisApi.resetCircuit(circuit.bridgeId, circuit.flow);
      message.success("Прекъсвачът е нулиран");
      await load(true);
    } catch (error) {
      message.error(error?.data?.error || "Грешка при нулиране");
    } finally {
      setBusyFlag(`circuit:${key}`, false);
    }
  };

  const openErrorDetail = async (row) => {
    setDetailRow(row);
    setDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const body = await nhisApi.getErrorBody(row.callLogId);
      setDetail(body);
    } catch (error) {
      message.error("Грешка при зареждане на детайлите");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleReview = async (row) => {
    setBusyFlag(`review:${row.callLogId}`, true);
    try {
      await nhisApi.reviewError(row.callLogId);
      message.success("Грешката е маркирана като прегледана");
      if (detailOpen) setDetailOpen(false);
      await load(true);
    } catch (error) {
      message.error(error?.data?.error || "Грешка при маркиране");
    } finally {
      setBusyFlag(`review:${row.callLogId}`, false);
    }
  };

  const o = data?.overview;

  const tiles = [
    {
      key: "bridge",
      icon: "activity",
      label: "Мост",
      value: o ? (o.bridgeOnline ? "Онлайн" : "Офлайн") : "—",
      sub: o?.lastSeen ? `Последен сигнал: ${fmtRelative(o.lastSeen)}` : "Няма сигнал",
      tone: o?.bridgeOnline ? "success" : "danger",
    },
    {
      key: "circuits",
      icon: "zap-off",
      label: "Отворени прекъсвачи",
      value: o ? o.openCircuits : "—",
      sub: "спрени потоци",
      tone: o?.openCircuits > 0 ? "danger" : "success",
    },
    {
      key: "failed",
      icon: "alert-triangle",
      label: "Неуспешни синхронизации",
      value: o ? o.failed : "—",
      sub: `${o?.queued ?? 0} чакащи · ${o?.claimed ?? 0} в обработка`,
      tone: o?.failed > 0 ? "warning" : "success",
    },
    {
      key: "errors",
      icon: "alert-octagon",
      label: "Грешки (24ч)",
      value: o ? o.errors24h : "—",
      sub: "от НЗИС",
      tone: o?.errors24h > 0 ? "warning" : "success",
    },
  ];

  const toneColor = {
    success: "#1e7e34",
    danger: "#c92a2a",
    warning: "#a86e00",
    neutral: "#6b7280",
  };

  return (
    <>
      <Header />
      <Sidebar id="menu-item" id1="menu-items" activeClassName="admin-dashboard" />
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Табло</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Мониторинг</li>
                </ul>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-primary btn-sm" onClick={() => load()} disabled={loading}>
                  <FeatherIcon icon="refresh-cw" size={15} className="me-1" /> Обнови
                </button>
              </div>
            </div>
          </div>

          {/* Greeting hero */}
          <div className="good-morning-blk">
            <div className="row">
              <div className="col-md-6">
                <div className="morning-user">
                  <h2>Здравейте</h2>
                  <p>Мониторинг на състоянието на системата и моста към НЗИС</p>
                </div>
              </div>
              <div className="col-md-6 position-blk">
                <div className="morning-img">
                  <img src={morning_img_01} alt="" />
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
              <Spin size="large" />
            </div>
          ) : !data ? (
            <div className="alert alert-danger">Данните за мониторинг не са налични.</div>
          ) : (
            <>
              {/* KPI tiles */}
              <div className="row">
                {tiles.map((t) => (
                  <div className="col-xl-3 col-sm-6 col-12" key={t.key}>
                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 48, height: 48, background: `${toneColor[t.tone]}1a`, color: toneColor[t.tone] }}
                          >
                            <FeatherIcon icon={t.icon} size={22} />
                          </div>
                          <div className="ms-3">
                            <h3 className="mb-0" style={{ color: toneColor[t.tone] }}>{t.value}</h3>
                            <h6 className="mb-0 text-muted">{t.label}</h6>
                          </div>
                        </div>
                        <small className="text-muted d-block mt-2">{t.sub}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bridge health */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h4 className="card-title mb-0">Състояние на моста</h4>
                    </div>
                    <div className="card-body">
                      {data.bridges.length === 0 ? (
                        <p className="text-muted mb-0">Няма регистриран мост.</p>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead>
                              <tr>
                                <th>Мост</th>
                                <th>Състояние</th>
                                <th>Последен сигнал</th>
                                <th>Версия</th>
                                <th>Сертификат (КЕП)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.bridges.map((b) => (
                                <tr key={b.bridgeId}>
                                  <td><strong>{b.bridgeId}</strong></td>
                                  <td>{b.online ? <Badge tone="success">Онлайн</Badge> : <Badge tone="danger">Офлайн</Badge>}</td>
                                  <td>{fmtRelative(b.lastSeen)}</td>
                                  <td>{b.version || "—"}</td>
                                  <td>
                                    {certBadge(b.certStatus)}
                                    {b.kepCertExpiresAt && (
                                      <small className="text-muted ms-2">{fmtDateTime(b.kepCertExpiresAt)}</small>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Circuit breakers */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h4 className="card-title mb-0">Прекъсвачи</h4>
                    </div>
                    <div className="card-body">
                      {data.circuits.length === 0 ? (
                        <p className="text-muted mb-0">Няма конфигурирани прекъсвачи.</p>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead>
                              <tr>
                                <th>Мост</th>
                                <th>Поток</th>
                                <th>Състояние</th>
                                <th>Причина</th>
                                <th>Сработил</th>
                                <th>HTTP</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.circuits.map((c) => {
                                const open = String(c.state).toUpperCase() === "OPEN";
                                const key = `circuit:${c.bridgeId}:${c.flow}`;
                                return (
                                  <tr key={`${c.bridgeId}-${c.flow}`}>
                                    <td>{c.bridgeId}</td>
                                    <td>{c.flow}</td>
                                    <td>{open ? <Badge tone="danger">Отворен</Badge> : <Badge tone="success">Затворен</Badge>}</td>
                                    <td className="text-muted" style={{ maxWidth: 280, whiteSpace: "normal" }}>{open ? (c.tripReason || "—") : "—"}</td>
                                    <td>{open ? fmtRelative(c.trippedAt) : "—"}</td>
                                    <td>{c.lastHttpStatus || "—"}</td>
                                    <td className="text-end">
                                      {open && (
                                        <button
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => handleReset(c)}
                                          disabled={busy[key]}
                                        >
                                          Нулирай
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Failed syncs */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h4 className="card-title mb-0">Синхронизации</h4>
                    </div>
                    <div className="card-body">
                      {/* Queue counts */}
                      <div className="d-flex flex-wrap gap-3 mb-4">
                        {[
                          { label: "Чакащи", value: o.queued, color: "#6b7280" },
                          { label: "Обработват се", value: o.claimed, color: "#1971c2" },
                          { label: "Приключени", value: o.completed, color: "#1e7e34" },
                          { label: "Неуспешни", value: o.failed, color: "#c92a2a" },
                        ].map((q) => (
                          <div
                            key={q.label}
                            className="d-flex align-items-center gap-2 px-3 py-2 rounded"
                            style={{ background: `${q.color}14`, minWidth: 140 }}
                          >
                            <span style={{ fontSize: 22, fontWeight: 700, color: q.color, lineHeight: 1 }}>{q.value}</span>
                            <span className="text-muted" style={{ fontSize: 13 }}>{q.label}</span>
                          </div>
                        ))}
                      </div>

                      <h6 className="text-muted mb-2">Неуспешни синхронизации</h6>
                      {data.failedJobs.length === 0 ? (
                        <div className="text-center py-3 text-muted">
                          <FeatherIcon icon="check-circle" size={32} className="mb-2" />
                          <p className="mb-0">Няма неуспешни синхронизации.</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead>
                              <tr>
                                <th>Пациент</th>
                                <th>Тип</th>
                                <th>Формат</th>
                                <th>Следващ опит</th>
                                <th>Създадена</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.failedJobs.map((j) => (
                                <tr key={j.jobId}>
                                  <td>{j.patient}</td>
                                  <td>{j.syncType || "—"}</td>
                                  <td>{j.apiSurface || "—"}</td>
                                  <td>{fmtDateTime(j.nextAttemptAt)}</td>
                                  <td>{fmtDateTime(j.createdAt)}</td>
                                  <td className="text-end">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handleRetry(j)}
                                      disabled={busy[`job:${j.jobId}`]}
                                    >
                                      Повтори
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent errors */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h4 className="card-title mb-0">Скорошни грешки</h4>
                    </div>
                    <div className="card-body">
                      {data.errors.length === 0 ? (
                        <div className="text-center py-3 text-muted">
                          <FeatherIcon icon="check-circle" size={32} className="mb-2" />
                          <p className="mb-0">Няма скорошни грешки.</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead>
                              <tr>
                                <th>Време</th>
                                <th>Пациент</th>
                                <th>Тип</th>
                                <th>HTTP</th>
                                <th>Грешка</th>
                                <th>Описание</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.errors.map((e, i) => (
                                <tr key={i}>
                                  <td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(e.attemptedAt)}</td>
                                  <td>{e.patient}</td>
                                  <td>{e.syncType || "—"}</td>
                                  <td>{e.httpStatus || "—"}</td>
                                  <td>{e.businessErrorType || "—"}</td>
                                  <td className="text-muted" style={{ maxWidth: 360, whiteSpace: "normal" }}>{e.message || "—"}</td>
                                  <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                                    <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => openErrorDetail(e)}>
                                      Детайли
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => handleReview(e)}
                                      disabled={busy[`review:${e.callLogId}`]}
                                      title="Маркирай като прегледана — премахва я от таблото"
                                    >
                                      Прегледано
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        title="Детайли за грешката"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={
          detailRow ? (
            <div className="text-end">
              <button
                className="btn btn-outline-success btn-sm"
                onClick={() => handleReview(detailRow)}
                disabled={busy[`review:${detailRow.callLogId}`]}
              >
                Маркирай като прегледано
              </button>
            </div>
          ) : null
        }
        width={820}
      >
        {detailRow && (
          <p className="text-muted mb-3">
            {fmtDateTime(detailRow.attemptedAt)} · {detailRow.syncType || "—"} · HTTP {detailRow.httpStatus || "—"} · {detailRow.patient}
          </p>
        )}
        {detailLoading ? (
          <div className="text-center py-4"><Spin /></div>
        ) : detail ? (
          <>
            <h6>Заявка</h6>
            <pre style={preStyle}>{detail.requestBody || "—"}</pre>
            <h6 className="mt-3">Отговор</h6>
            <pre style={preStyle}>{detail.responseBody || "—"}</pre>
          </>
        ) : (
          <p className="text-muted mb-0">Няма запазено тяло за тази грешка.</p>
        )}
      </Modal>
    </>
  );
};

const preStyle = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 280,
  overflow: "auto",
  background: "#f5f7fb",
  border: "1px solid #e6eaf2",
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 12,
  margin: 0,
};

export default Admin_Dashboard;
